import { skipToken, useQuery } from '@tanstack/react-query';
import { kpiApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  KpiSummaryParams,
  KpiTrendParams,
  KpiWeeklyTrendParams,
  BugDetailParams,
  BugBreakdownParams,
  BugBreakdownDetailsParams,
  BugSeverityParams,
  BugEscapeTimingParams,
  BugEscapeTimingDetailsParams,
  ReleaseDetailParams,
  DevelopPointDetailParams,
} from '../types/api.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business KPIサマリーデータをTanStack Query経由で取得。チーム・日付指定でバグ率/リリースPT/開発PTの集計結果を返す
 * @graph-connects /kpi/summary [calls] KPIサマリーAPI呼び出し
 * @graph-connects kpiApi.getSummary [calls] KPIサマリーAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiSummary(params?: KpiSummaryParams) {
  return useQuery({
    queryKey: queryKeys.kpi.summary(params),
    queryFn: () => kpiApi.getSummary(params),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business KPIトレンドデータをTanStack Query経由で取得。チーム・期間指定で日次KPI推移を返す
 * @graph-connects /kpi/trend [calls] KPIトレンドAPI呼び出し
 * @graph-connects kpiApi.getTrend [calls] KPIトレンドAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiTrend(params?: KpiTrendParams) {
  return useQuery({
    queryKey: queryKeys.kpi.trend(params),
    queryFn: () => kpiApi.getTrend(params),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次pt集計データをTanStack Query経由で取得。チーム・週数指定で週単位の開発pt 目標/実績/月内累計/達成率を返す
 * @graph-connects /kpi/weekly-trend [calls] 週次pt集計API呼び出し
 * @graph-connects kpiApi.getWeeklyTrend [calls] 週次pt集計APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiWeeklyTrend(params?: KpiWeeklyTrendParams) {
  return useQuery({
    queryKey: queryKeys.kpi.weeklyTrend(params),
    queryFn: () => kpiApi.getWeeklyTrend(params),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business fibo予算シート（直近2FYマージ）の月別 PDG/ACE 開発pt予算をTanStack Query経由で取得。メンバー管理ページの Qサマリーで予算達成率の分母に使用
 * @graph-connects /kpi/monthly-budgets [calls] fibo予算 月次開発pt予算API呼び出し
 * @graph-connects kpiApi.getMonthlyBudgets [calls] fibo予算 月次開発pt予算APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMonthlyDevelopPointBudgets() {
  return useQuery({
    queryKey: queryKeys.kpi.monthlyBudgets(),
    queryFn: () => kpiApi.getMonthlyBudgets(),
    // 予算はサーバ側でも 1 時間キャッシュされる月次データのため、頻繁な再取得は不要
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ詳細データをTanStack Query経由で取得。チーム・日付範囲でバグ一覧のドリルダウンを返す
 * @graph-connects /kpi/bugs [calls] バグ詳細API呼び出し
 * @graph-connects kpiApi.getBugs [calls] バグ詳細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiBugs(params: BugDetailParams | null) {
  return useQuery({
    queryKey: queryKeys.kpi.bugs(params ?? undefined),
    queryFn: () => kpiApi.getBugs(params ?? undefined),
    enabled: params !== null,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ検知日数詳細データをTanStack Query経由で取得。チーム・発見日（初回発生日優先・空なら起票日にフォールバック）範囲で案件起因バグ一覧のドリルダウンを返す（バグ検知日数分布チャート・ドリルダウンモーダル用）
 * @graph-connects /kpi/bug-detection-days [calls] バグ検知日数詳細API呼び出し
 * @graph-connects kpiApi.getBugDetectionDays [calls] バグ検知日数詳細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiBugDetectionDays(params: BugDetailParams | null) {
  return useQuery({
    queryKey: queryKeys.kpi.bugDetectionDays(params ?? undefined),
    queryFn: () => kpiApi.getBugDetectionDays(params ?? undefined),
    enabled: params !== null,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ内訳データをTanStack Query経由で取得。信頼性ページ用に直近Nヶ月のカテゴリ別バグ件数＋捕捉率を集計単位（unit: 月次/週次・月曜始まり）別に返す
 * @graph-connects /kpi/bug-breakdown [calls] バグ内訳API呼び出し
 * @graph-connects kpiApi.getBugBreakdown [calls] バグ内訳APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiBugBreakdown(params?: BugBreakdownParams) {
  return useQuery({
    queryKey: queryKeys.kpi.bugBreakdown(params),
    queryFn: () => kpiApi.getBugBreakdown(params),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 期間別バグ詳細データをTanStack Query経由で取得。信頼性ページのチャート期間クリック→ドリルダウンモーダル用に、指定起票月（または起票週）のバグ個別課題一覧をカテゴリ付きで返す。params が null の間は取得しない
 * @graph-connects /kpi/bug-breakdown/details [calls] 期間別バグ詳細API呼び出し
 * @graph-connects kpiApi.getBugBreakdownDetails [calls] 期間別バグ詳細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiBugBreakdownDetails(params: BugBreakdownDetailsParams | null) {
  return useQuery({
    queryKey: queryKeys.kpi.bugBreakdownDetails(params ?? undefined),
    // params が null の間は skipToken で取得しない。month/week の排他は
    // BugBreakdownDetailsParams（union）が型レベルで保証する
    queryFn: params === null ? skipToken : () => kpiApi.getBugBreakdownDetails(params),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 重大度別バグ件数をTanStack Query経由で取得。信頼性ページ用に直近Nヶ月の起票月（または起票週・月曜始まり）×インシデントレベル別件数を集計単位（unit）別に返す
 * @graph-connects /kpi/bug-severity [calls] 重大度別バグ件数API呼び出し
 * @graph-connects kpiApi.getBugSeverity [calls] 重大度別バグ件数APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiBugSeverity(params?: BugSeverityParams) {
  return useQuery({
    queryKey: queryKeys.kpi.bugSeverity(params),
    queryFn: () => kpiApi.getBugSeverity(params),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エスケープ・タイミング集計をTanStack Query経由で取得。信頼性ページ用に「起票日−起因案件リリース日」（KPI と同じ日付基準）の日数ビン集計を返す。team 指定で起因案件の開発リーダー所属に絞る
 * @graph-connects /kpi/bug-escape-timing [calls] エスケープ・タイミングAPI呼び出し
 * @graph-connects kpiApi.getBugEscapeTiming [calls] エスケープ・タイミングAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiBugEscapeTiming(params?: BugEscapeTimingParams) {
  return useQuery({
    queryKey: queryKeys.kpi.bugEscapeTiming(params),
    queryFn: () => kpiApi.getBugEscapeTiming(params),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エスケープ・タイミングのビン別バグ詳細をTanStack Query経由で取得。ビンクリック→ドリルダウンモーダル用。params が null の間は取得しない
 * @graph-connects /kpi/bug-escape-timing/details [calls] ビン別バグ詳細API呼び出し
 * @graph-connects kpiApi.getBugEscapeTimingDetails [calls] ビン別バグ詳細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiBugEscapeTimingDetails(params: BugEscapeTimingDetailsParams | null) {
  const bin = params?.bin;
  const months = params?.months;
  const team = params?.team;
  return useQuery({
    queryKey: queryKeys.kpi.bugEscapeTimingDetails(params ?? undefined),
    // enabled ガードにより bin が undefined のまま queryFn は呼ばれない
    queryFn: () => kpiApi.getBugEscapeTimingDetails({ bin: bin ?? 'beforeRelease', months, team }),
    enabled: bin !== undefined,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business リリース詳細データをTanStack Query経由で取得。チーム・日付範囲でリリース一覧のドリルダウンを返す
 * @graph-connects /kpi/releases [calls] リリース詳細API呼び出し
 * @graph-connects kpiApi.getReleases [calls] リリース詳細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiReleases(params: ReleaseDetailParams | null) {
  return useQuery({
    queryKey: queryKeys.kpi.releases(params ?? undefined),
    queryFn: () => kpiApi.getReleases(params ?? undefined),
    enabled: params !== null,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 開発ポイント詳細データをTanStack Query経由で取得。チーム・日付範囲で開発ポイント一覧のドリルダウンを返す
 * @graph-connects /kpi/develop-points [calls] 開発ポイント詳細API呼び出し
 * @graph-connects kpiApi.getDevelopPoints [calls] 開発ポイント詳細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKpiDevelopPoints(params: DevelopPointDetailParams | null) {
  return useQuery({
    queryKey: queryKeys.kpi.developPoints(params ?? undefined),
    queryFn: () => kpiApi.getDevelopPoints(params ?? undefined),
    enabled: params !== null,
  });
}
